/**
 * projectNickname.ts
 * Derived short names for project/WO labels — the P3 "nickname chips" of the
 * Employee Schedule redesign (2026-07-25). The lab talks in project shorthand
 * ("the Kitimat job"), and real labels run 25–40 chars ("Revelstoke Dam Core
 * Characterization"), so chips show the first distinctive words and the full
 * label lives in the tooltip. Derivation only for v1 — a per-project override
 * field can layer on later without changing callers.
 */

const MAX_CHARS = 16;

/**
 * "Kitimat LNG Foundation Investigation" → "Kitimat LNG"
 * "Revelstoke Dam Core Characterization" → "Revelstoke Dam"
 * "KSM 2026" → "KSM 2026" · "Time off" → "Time off"
 * Trailing "(WO-…)" / "(Q-…)" brackets are dropped first.
 */
export function deriveProjectNickname(label: string): string {
  const clean = label.replace(/\s*\([^)]*\)\s*$/, '').trim();
  if (!clean) return label.trim() || '—';
  const words = clean.split(/\s+/);
  // Take words until adding the next would pass the budget; always take one.
  let out = words[0];
  for (let i = 1; i < words.length; i++) {
    const next = `${out} ${words[i]}`;
    if (next.length > MAX_CHARS) break;
    out = next;
  }
  return out;
}

/**
 * Stable per-key chip colour index — callers map it onto theme-driven palette
 * entries so two projects in one plan rarely collide.
 */
export function nicknameColorIndex(key: string, paletteSize: number): number {
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) + hash) ^ key.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % Math.max(1, paletteSize);
}
